import type { InferResponse } from '@/hooks/useApiClient'
import { BookOutlined, FileTextOutlined, WarningFilled } from '@ant-design/icons'
import sparkles from '@assets/CustomIcons/sparkles.svg'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { addCommasToNumber, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { colorTypeMatch } from './util'
import type { SimpleRowProps } from './types'

export function SimpleRow({
  row,
  formattedCurrency,
  openOrderModal,
  showDate,
  isDraw,
  isAdjustment,
  isTemp,
}: SimpleRowProps) {
  const { scopes } = useSecurityContext()
  const orderReadScope = scopes?.scopes?.includes('o.r')
  const orderMvmtScope = scopes?.scopes?.includes('om.p')
  const hasMovementError = row?.has_errors || row?.missing_prices
  const orderMvmtButtonTitle = !hasMovementError
    ? 'View Order Movements'
    : row?.is_projected
      ? 'Missing or Expired Price'
      : row?.missing_prices
        ? 'BOL is missing prices'
        : 'Movement Has Validation Errors'

  function renderTitle(row: SimpleRowProps['row']) {
    if (isTemp) {
      return (
        <Texto className='text-truncate' appearance={'error'} weight={'bold'}>
          <WarningFilled style={{ color: 'var(--theme-error)' }} className={'mr-2'} />
          TEMP
        </Texto>
      )
    }
    if (isAdjustment) {
      return (
        <Horizontal>
          <Texto style={{ color: colorTypeMatch.ADDITIVE_ADJUSTMENT.color }}>ADDITIVE ADJ</Texto>
        </Horizontal>
      )
    }
    if (row.order_id) {
      return (
        <Horizontal alignItems='center'>
          {orderReadScope && (
            <GraviButton
              className='simple-row-action-button mr-2 p-2'
              icon={<FileTextOutlined />}
              onClick={() => openOrderModal?.(row.order_id)}
            />
          )}
          {orderMvmtScope && (
            <Link
              target='_blank'
              to={`/order_movements_page/report?${new URLSearchParams({ order_number: row.order_number })}`}
            >
              <GraviButton
                className='simple-row-action-button mr-2 p-2'
                error={hasMovementError}
                icon={<BookOutlined />}
                title={orderMvmtButtonTitle}
              />
            </Link>
          )}
        </Horizontal>
      )
    }
    return 'N/A'
  }

  return (
    <Horizontal
      className='simple-row-container bordered bg-1'
      width='100%'
      style={{
        borderLeft: isDraw ? '2px solid var(--theme-error)' : '2px solid var(--gray-500)',
      }}
      alignItems='center'
    >
      <Vertical className='p-2' flex={1.5}>
        {renderTitle(row)}
      </Vertical>
      <Horizontal className='p-2' flex={1.2} justifyContent='center' alignItems='center'>
        {row.is_projected && <img src={sparkles} className='mr-2' alt='sparkles' style={{ height: '12px' }} />}
        {row.date && showDate ? (
          showDate(row.date, row.is_projected)
        ) : (
          <Texto appearance={row?.is_projected ? 'secondary' : 'default'}>
            {row.date ? moment(row.date).format('MM/DD/YYYY') : 'N/A'}
          </Texto>
        )}
      </Horizontal>
      <Vertical className='p-2' flex={1} justifyContent='center' alignItems='center'>
        <Texto className='text-truncate' align='center' style={{ width: '100%' }}>
          {(!isTemp && row.bol_numbers?.[0]) || 'N/A'}
        </Texto>
      </Vertical>
      <Vertical className='p-2' flex={1} justifyContent='center' alignItems='center'>
        {(!isTemp && row.order_number) || 'N/A'}
      </Vertical>
      <Vertical className='p-2' flex={1.5} justifyContent='center' alignItems='center'>
        <Texto className='text-truncate' align='center' style={{ width: '100%' }}>
          {(!isTemp && row.suppliers?.[0]) || 'N/A'}
        </Texto>
      </Vertical>
      <Vertical className='p-2' flex={1} justifyContent='center' alignItems='flex-end'>
        <Texto className='text-truncate full-height-width ' align='right'>
          {addCommasToNumber(isAdjustment ? Math.abs(row?.volume) : row?.volume, 0) ?? 'N/A'}
        </Texto>
      </Vertical>
      <Vertical className='p-2' flex={1} justifyContent='center' alignItems='flex-end'>
        <Texto className='text-truncate full-height-width ' align='right'>
          {formattedCurrency}
          {addCommasToNumber(isAdjustment && row?.price ? Math.abs(row?.price) : row?.price, 4) ?? 'N/A'}
        </Texto>
      </Vertical>
      <Vertical className='p-2 mr-2' flex={1} justifyContent='center' alignItems='flex-end'>
        <Texto className='text-truncate full-height-width ' align='right' appearance={isDraw ? 'error' : 'default'}>
          {formattedCurrency}
          {addCommasToNumber(isAdjustment ? Math.abs(row?.value) : row?.value, 2) ?? 'N/A'}
        </Texto>
      </Vertical>
    </Horizontal>
  )
}
